import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { ShieldCheck, Users, ArrowLeft, ArrowRight } from "lucide-react";

const inputCls =
  "w-full bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-4 py-3 rounded-sm text-white transition-colors";

const ROLES = [
  { key: "player", title: "Player", desc: "Post highlights, track stats, get seen by scouts." },
  { key: "scout", title: "Scout", desc: "Discover and contact talent. 18+ only. Verified badge after review." },
  { key: "coach", title: "Coach", desc: "Follow your squad and find players. 18+ only." },
];

export function computeAge(dob) {
  if (!dob) return null;
  const b = new Date(dob);
  if (isNaN(b)) return null;
  const t = new Date();
  let age = t.getFullYear() - b.getFullYear();
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) age--;
  return age;
}

export function tierFor(age) {
  if (age === null || age < 0 || age > 100) return null;
  if (age < 13) return {
    key: "parent_run", label: "Under 13 — Parent-run account",
    note: "A parent creates and fully runs this account: the login, posts and all messages belong to the parent.",
  };
  if (age < 16) return {
    key: "youth_13_15", label: "13–15 — Youth account",
    note: "A parent's email must be linked. The parent can see all account activity, including messages.",
  };
  if (age < 18) return {
    key: "youth_16_17", label: "16–17 — Youth account",
    note: "No parent needed. Only verified scouts can message you directly — everyone else must send a request you approve.",
  };
  return { key: "full", label: "18+ — Full access", note: "Full access to all EGNCY features." };
}

export default function RegisterPage() {
  const { register, formatApiError } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    role: "", dob: "", parent_name: "", parent_email: "",
    name: "", email: "", password: "", position: "", team: "", country: "", city: "", region: "",
  });
  const [regions, setRegions] = useState({});

  useEffect(() => {
    api.get("/regions").then(({ data }) => setRegions(data.Australia || {})).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target?.value ?? e }));
  const age = useMemo(() => computeAge(form.dob), [form.dob]);
  const tier = useMemo(() => (form.role === "player" ? tierFor(age) : null), [form.role, age]);
  const isMinorStaff = form.role !== "player" && age !== null && age < 18;
  const underMinAge = form.role === "player" && age !== null && age >= 0 && age < 16;

  const canLeaveStep2 =
    form.dob && age !== null && age >= 0 && age <= 100 && !isMinorStaff && !underMinAge &&
    (form.role !== "player" ||
      (tier?.key !== "parent_run" || (form.parent_name && form.parent_email)) &&
      (tier?.key !== "youth_13_15" || form.parent_email));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({
        ...form,
        parent_name: form.parent_name || undefined,
        parent_email: form.parent_email || undefined,
      });
      nav(form.role === "player" ? "/welcome" : "/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-lg">
        <h1 className="font-display text-5xl uppercase tracking-tight text-white mb-1">EGNCY</h1>
        <p className="text-sm text-[#a3a3a3] mb-6">Where footballers get seen. Step {step} of 3.</p>
        <div className="flex gap-1 mb-6">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1 flex-1 rounded-sm ${s <= step ? "bg-white" : "bg-[#262626]"}`} />
          ))}
        </div>

        <form onSubmit={submit} className="bg-[#121212] border border-[#262626] rounded-sm p-6 space-y-4">
          {error && (
            <div data-testid="register-error" className="text-sm text-red-400 border border-red-500/40 bg-red-500/10 p-3 rounded-sm">
              {error}
            </div>
          )}

          {step === 1 && (
            <>
              <div className="text-xs uppercase tracking-widest text-[#737373]">
                I am a… <span className="text-white">(this cannot be changed later)</span>
              </div>
              {ROLES.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  data-testid={`register-role-${r.key}`}
                  onClick={() => { setForm((f) => ({ ...f, role: r.key })); setStep(2); }}
                  className={`w-full text-left p-4 border rounded-sm transition-colors ${
                    form.role === r.key ? "border-white bg-[#1a1a1a]" : "border-[#262626] hover:border-white"
                  }`}
                >
                  <div className="font-display text-xl uppercase tracking-wide text-white">{r.title}</div>
                  <div className="text-xs text-[#a3a3a3] mt-1">{r.desc}</div>
                </button>
              ))}
            </>
          )}

          {step === 2 && (
            <>
              <label className="block">
                <div className="text-xs uppercase tracking-widest text-[#737373] mb-1">
                  Date of birth <span className="text-white">(cannot be changed later)</span>
                </div>
                <input
                  data-testid="register-dob"
                  type="date"
                  value={form.dob}
                  onChange={set("dob")}
                  max={new Date().toISOString().slice(0, 10)}
                  className={inputCls}
                  required
                />
              </label>

              {isMinorStaff && (
                <div className="text-sm text-red-400 border border-red-500/40 bg-red-500/10 p-3 rounded-sm" data-testid="register-staff-age-error">
                  Scouts and coaches must be 18 or older.
                </div>
              )}

              {underMinAge && (
                <div className="text-sm text-yellow-300 border border-yellow-400/40 bg-yellow-400/10 p-3 rounded-sm" data-testid="register-underage-notice">
                  EGNCY is currently open to players aged <span className="font-semibold">16 and over</span>.
                  Under-16 accounts (with parent-managed safety features) are coming soon — check back shortly!
                </div>
              )}

              {tier && !underMinAge && (
                <div data-testid={`register-tier-${tier.key}`} className="border border-[#404040] bg-[#0a0a0a] p-4 rounded-sm">
                  <div className="flex items-center gap-2 text-white text-sm font-semibold">
                    <ShieldCheck className="w-4 h-4" />
                    {tier.label}
                  </div>
                  <p className="text-xs text-[#a3a3a3] mt-1">{tier.note}</p>
                </div>
              )}

              {tier?.key === "parent_run" && !underMinAge && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#737373]">
                    <Users className="w-3.5 h-3.5" /> Parent / guardian (runs this account)
                  </div>
                  <input data-testid="register-parent-name" value={form.parent_name} onChange={set("parent_name")} placeholder="Parent's full name" className={inputCls} />
                  <input data-testid="register-parent-email" type="email" value={form.parent_email} onChange={set("parent_email")} placeholder="Parent's email" className={inputCls} />
                </div>
              )}
              {tier?.key === "youth_13_15" && !underMinAge && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#737373]">
                    <Users className="w-3.5 h-3.5" /> Linked parent (sees all account activity)
                  </div>
                  <input data-testid="register-parent-email" type="email" value={form.parent_email} onChange={set("parent_email")} placeholder="Parent's email" className={inputCls} />
                </div>
              )}

              <div className="flex justify-between pt-1">
                <StepBtn onClick={() => setStep(1)} back>Back</StepBtn>
                <StepBtn testid="register-next-2" onClick={() => canLeaveStep2 && setStep(3)} disabled={!canLeaveStep2}>Continue</StepBtn>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <input data-testid="register-name" value={form.name} onChange={set("name")} placeholder={tier?.key === "parent_run" ? "Player's full name" : "Full name"} className={inputCls} required />
              <input data-testid="register-email" type="email" value={form.email} onChange={set("email")} placeholder={tier?.key === "parent_run" ? "Login email (parent's)" : "Email"} className={inputCls} required />
              <input data-testid="register-password" type="password" value={form.password} onChange={set("password")} placeholder="Password (min 6 characters)" className={inputCls} required minLength={6} />
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="register-country" value={form.country} onChange={set("country")} placeholder="Country" className={inputCls} required />
                <input data-testid="register-city" value={form.city} onChange={set("city")} placeholder="City" className={inputCls} required />
              </div>
              {form.role === "player" && (
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="register-position" value={form.position} onChange={set("position")} placeholder="Position (e.g. ST)" className={inputCls} />
                  <input data-testid="register-team" value={form.team} onChange={set("team")} placeholder="Team" className={inputCls} />
                </div>
              )}
              {form.role === "player" && (
                <label className="block">
                  <div className="text-xs uppercase tracking-widest text-[#737373] mb-1">
                    Region (Australia) — helps scouts find you
                  </div>
                  <select data-testid="register-region" value={form.region} onChange={set("region")} className={inputCls}>
                    <option value="">Not in Australia / skip for now</option>
                    {Object.entries(regions).map(([state, areas]) => (
                      <optgroup key={state} label={state}>
                        {areas.map((a) => (
                          <option key={a} value={`${state} - ${a}`}>{`${state} - ${a}`}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </label>
              )}
              <div className="flex justify-between pt-1">
                <StepBtn onClick={() => setStep(2)} back>Back</StepBtn>
                <button
                  data-testid="register-submit"
                  disabled={loading}
                  className="px-6 py-2.5 bg-white text-black font-semibold rounded-sm hover:bg-gray-200 disabled:opacity-60 transition-colors"
                >
                  {loading ? "Creating…" : "Create account"}
                </button>
              </div>
            </>
          )}
        </form>

        <p className="text-xs text-[#737373] mt-4" data-testid="register-legal-disclaimer">
          By creating an account, you agree to our{" "}
          <Link to="/terms" data-testid="register-terms-link" className="text-[#a3a3a3] underline hover:text-white">Terms of Service</Link>{" "}
          and{" "}
          <Link to="/privacy" data-testid="register-privacy-link" className="text-[#a3a3a3] underline hover:text-white">Privacy Policy</Link>.
        </p>

        <p className="text-sm text-[#737373] mt-4">
          Already on EGNCY?{" "}
          <Link to="/login" data-testid="register-login-link" className="text-white hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
}

function StepBtn({ children, onClick, disabled, back, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-sm text-sm transition-colors disabled:opacity-40 ${
        back ? "border border-[#262626] text-[#a3a3a3] hover:text-white" : "bg-white text-black font-semibold hover:bg-gray-200"
      }`}
    >
      {back && <ArrowLeft className="w-4 h-4" />}
      {children}
      {!back && <ArrowRight className="w-4 h-4" />}
    </button>
  );
}
